import React, { useEffect, useState } from "react";
import api from "../lib/api";
import FeaturedHero from "../components/FeaturedHero";
import NewsCard from "../components/NewsCard";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { ArrowRight, Flame, Users, BarChart2, TrendingUp, TrendingDown, Wallet } from "lucide-react";

function FearGreedWidget() {
  const [data, setData] = useState(null);

  useEffect(() => {
    fetch("https://api.alternative.me/fng/?limit=1")
      .then((r) => r.json())
      .then((d) => setData(d.data[0]))
      .catch(() => {});
  }, []);

  if (!data) return null;

  const value = parseInt(data.value);
  const label = data.value_classification;

  const getColor = () => {
    if (value <= 25) return { text: "text-red-400", bar: "bg-red-400", border: "border-red-400/30" };
    if (value <= 45) return { text: "text-orange-400", bar: "bg-orange-400", border: "border-orange-400/30" };
    if (value <= 55) return { text: "text-yellow-400", bar: "bg-yellow-400", border: "border-yellow-400/30" };
    if (value <= 75) return { text: "text-lime-400", bar: "bg-lime-400", border: "border-lime-400/30" };
    return { text: "text-green-400", bar: "bg-green-400", border: "border-green-400/30" };
  };

  const c = getColor();

  return (
    <div className={"bg-[var(--bg-panel)] border p-5 " + c.border}>
      <div className="flex items-center justify-between mb-3">
        <span className="font-mono text-xs uppercase tracking-[0.2em] text-[var(--text-tertiary)]">Fear & Greed</span>
        <span className="font-mono text-xs text-[var(--text-tertiary)]">Today</span>
      </div>
      <div className="flex items-center gap-4">
        <div className={"font-display font-bold text-5xl " + c.text}>{value}</div>
        <div>
          <div className={"font-bold text-sm " + c.text}>{label}</div>
          <div className="w-32 bg-zinc-800 h-2 rounded-full mt-2">
            <div className={"h-2 rounded-full transition-all " + c.bar} style={{ width: value + "%" }} />
          </div>
        </div>
      </div>
    </div>
  );
}

function PollWidget() {
  const [poll, setPoll] = useState(null);
  const [voted, setVoted] = useState(false);
  const [votedOption, setVotedOption] = useState(null);
  const [loading, setLoading] = useState(true);
  const [voting, setVoting] = useState(false);
  const [error, setError] = useState(null);

  const fetchPoll = () => {
    api.get("/polls/active")
      .then((r) => {
        setPoll(r.data);
        const savedVote = localStorage.getItem("poll_voted_" + r.data.id);
        if (savedVote) {
          setVoted(true);
          setVotedOption(savedVote);
        }
      })
      .catch(() => setPoll(null))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchPoll();
    const interval = setInterval(fetchPoll, 30000);
    return () => clearInterval(interval);
  }, []);

  const totalVotes = poll ? poll.options.reduce((sum, o) => sum + o.votes, 0) : 0;

  const handleVote = async (option) => {
    if (voted || voting) return;
    setVoting(true);
    try {
      const res = await api.post("/polls/" + poll.id + "/vote", { option });
      setPoll(res.data);
      setVoted(true);
      setVotedOption(option);
      localStorage.setItem("poll_voted_" + poll.id, option);
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not submit vote");
    } finally {
      setVoting(false);
    }
  };

  if (loading || !poll) return null;

  const colors = [
    { bar: "bg-yellow-400", text: "text-yellow-400", border: "border-yellow-400/40", bg: "bg-yellow-400/10" },
    { bar: "bg-blue-500", text: "text-blue-400", border: "border-blue-500/40", bg: "bg-blue-500/10" },
    { bar: "bg-green-500", text: "text-green-400", border: "border-green-500/40", bg: "bg-green-500/10" },
    { bar: "bg-red-500", text: "text-red-400", border: "border-red-500/40", bg: "bg-red-500/10" },
    { bar: "bg-purple-500", text: "text-purple-400", border: "border-purple-500/40", bg: "bg-purple-500/10" },
    { bar: "bg-pink-500", text: "text-pink-400", border: "border-pink-500/40", bg: "bg-pink-500/10" },
  ];

  const daysLeft = poll.ends_at
    ? Math.max(0, Math.ceil((new Date(poll.ends_at) - new Date()) / (1000 * 60 * 60 * 24)))
    : null;

  return (
    <section className="max-w-[1400px] mx-auto px-6 md:px-10 mt-20">
      <div className="bg-[var(--bg-panel)] border border-[var(--border-soft)] p-8">
        <div className="flex items-center gap-2 mb-2">
          <BarChart2 className="w-5 h-5 text-yellow-400" />
          <h3 className="font-mono text-xs uppercase tracking-[0.2em] text-[var(--text-tertiary)]">Community Poll</h3>
          {daysLeft !== null && (
            <span className="ml-auto font-mono text-xs text-[var(--text-tertiary)]">
              {daysLeft > 0 ? daysLeft + " days left" : "Poll ended"}
            </span>
          )}
        </div>
        <h2 className="font-display font-bold text-xl md:text-2xl text-[var(--text-primary)] mb-2">
          {poll.question}
        </h2>
        <p className="text-xs font-mono text-[var(--text-tertiary)] mb-6">
          {totalVotes.toLocaleString()} votes · {voted ? "You voted" : "Click to vote"}
        </p>
        <div className="space-y-3">
          {poll.options.map((option, index) => {
            const pct = totalVotes > 0 ? Math.round((option.votes / totalVotes) * 100) : 0;
            const c = colors[index % colors.length];
            const isMyVote = votedOption === option.label;
            return (
              <button
                key={option.label}
                onClick={() => handleVote(option.label)}
                disabled={voted || voting}
                className={"w-full text-left relative overflow-hidden border transition " +
                  c.border +
                  (isMyVote ? " ring-1 ring-yellow-400" : "") +
                  (voted ? " cursor-default" : " cursor-pointer") +
                  " disabled:cursor-default"
                }
              >
                <div className={"absolute inset-0 transition-all duration-700 " + c.bar + " opacity-20"} style={{ width: pct + "%" }} />
                <div className="relative flex items-center justify-between px-4 py-3">
                  <div className="flex items-center gap-2">
                    {isMyVote && <span className="text-yellow-400 text-xs">✓</span>}
                    <span className={"font-medium text-sm " + (isMyVote ? "text-[var(--text-primary)]" : "text-[var(--text-secondary)]")}>{option.label}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-xs font-mono text-[var(--text-tertiary)]">{option.votes} votes</span>
                    <span className={"font-mono font-bold text-sm " + c.text}>{pct}%</span>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
        {error && <p className="text-red-400 text-xs mt-3 font-mono">{error}</p>}
        <div className="mt-4 flex items-center justify-between">
          <span className="text-xs font-mono text-[var(--text-tertiary)]">{totalVotes.toLocaleString()} total votes · updates every 30s</span>
          {voted && <span className="text-xs font-mono text-yellow-400">✓ Vote recorded</span>}
        </div>
      </div>
    </section>
  );
}

function GainersLosers() {
  const [prices, setPrices] = useState([]);

  const COINS = [
    { symbol: "BTCUSDT", ticker: "BTC", name: "Bitcoin", slug: "bitcoin" },
    { symbol: "ETHUSDT", ticker: "ETH", name: "Ethereum", slug: "ethereum" },
    { symbol: "SOLUSDT", ticker: "SOL", name: "Solana", slug: "solana" },
    { symbol: "XRPUSDT", ticker: "XRP", name: "XRP", slug: "xrp" },
    { symbol: "BNBUSDT", ticker: "BNB", name: "BNB", slug: "bnb" },
    { symbol: "ADAUSDT", ticker: "ADA", name: "Cardano", slug: "cardano" },
    { symbol: "DOGEUSDT", ticker: "DOGE", name: "Dogecoin", slug: "dogecoin" },
    { symbol: "AVAXUSDT", ticker: "AVAX", name: "Avalanche", slug: "avalanche" },
    { symbol: "LINKUSDT", ticker: "LINK", name: "Chainlink", slug: "chainlink" },
    { symbol: "DOTUSDT", ticker: "DOT", name: "Polkadot", slug: "polkadot" },
    { symbol: "NEARUSDT", ticker: "NEAR", name: "NEAR", slug: "near" },
    { symbol: "INJUSDT", ticker: "INJ", name: "Injective", slug: "injective" },
    { symbol: "SUIUSDT", ticker: "SUI", name: "Sui", slug: "sui" },
    { symbol: "APTUSDT", ticker: "APT", name: "Aptos", slug: "aptos" },
    { symbol: "ARBUSDT", ticker: "ARB", name: "Arbitrum", slug: "arbitrum" },
    { symbol: "OPUSDT", ticker: "OP", name: "Optimism", slug: "optimism" },
    { symbol: "ATOMUSDT", ticker: "ATOM", name: "Cosmos", slug: "atom" },
    { symbol: "PEPEUSDT", ticker: "PEPE", name: "Pepe", slug: "pepe" },
    { symbol: "SHIBUSDT", ticker: "SHIB", name: "Shiba Inu", slug: "shiba-inu" },
    { symbol: "TONUSDT", ticker: "TON", name: "Toncoin", slug: "ton" },
  ];

  useEffect(() => {
    fetch("https://api.binance.com/api/v3/ticker/24hr")
      .then((r) => r.json())
      .then((data) => {
        const symbols = COINS.map((c) => c.symbol);
        const filtered = data
          .filter((d) => symbols.includes(d.symbol))
          .map((d) => {
            const coin = COINS.find((c) => c.symbol === d.symbol);
            return { ...coin, price: parseFloat(d.lastPrice), change: parseFloat(d.priceChangePercent) };
          });
        setPrices(filtered);
      })
      .catch(() => {});
  }, []);

  const sorted = [...prices].sort((a, b) => b.change - a.change);
  const gainers = sorted.slice(0, 3);
  const losers = sorted.slice(-3).reverse();

  if (prices.length === 0) return null;

  const formatPrice = (p) => {
    if (p < 1) return "$" + p.toFixed(4);
    return "$" + p.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const CoinRow = ({ coin }) => (
    <Link to={"/coins/" + coin.slug} className="flex items-center justify-between py-2 hover:bg-[var(--bg-elevated)] px-2 transition group">
      <div className="flex items-center gap-2">
        <span className="font-mono text-xs text-[var(--text-tertiary)] w-10">{coin.ticker}</span>
        <span className="text-xs text-[var(--text-secondary)] group-hover:text-[var(--text-primary)] transition hidden md:block">{coin.name}</span>
      </div>
      <div className="flex items-center gap-3">
        <span className="font-mono text-xs text-[var(--text-primary)]">{formatPrice(coin.price)}</span>
        <span className={"font-mono text-xs font-bold " + (coin.change >= 0 ? "text-green-400" : "text-red-400")}>
          {coin.change >= 0 ? "+" : ""}{coin.change.toFixed(2)}%
        </span>
      </div>
    </Link>
  );

  return (
    <div className="mt-6 border-t border-[var(--border-soft)] pt-6">
      <FearGreedWidget />
      <div className="mt-6">
        <div className="flex items-center gap-2 mb-2">
          <TrendingUp className="w-4 h-4 text-green-400" />
          <span className="font-mono text-xs uppercase tracking-[0.2em] text-[var(--text-tertiary)]">Top Gainers</span>
        </div>
        <div className="space-y-1">
          {gainers.map((coin) => <CoinRow key={coin.symbol} coin={coin} />)}
        </div>
      </div>
      <div className="mt-4">
        <div className="flex items-center gap-2 mb-2">
          <TrendingDown className="w-4 h-4 text-red-400" />
          <span className="font-mono text-xs uppercase tracking-[0.2em] text-[var(--text-tertiary)]">Top Losers</span>
        </div>
        <div className="space-y-1">
          {losers.map((coin) => <CoinRow key={coin.symbol} coin={coin} />)}
        </div>
      </div>
      <Link to="/coins" className="block mt-4 text-xs font-mono text-[var(--text-tertiary)] hover:text-yellow-400 transition text-center">
        View all 100 coins →
      </Link>
    </div>
  );
}

export default function Home() {
  const { user } = useAuth();
  const [news, setNews] = useState([]);
  const [trending, setTrending] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [a, b] = await Promise.all([
          api.get("/news?limit=20"),
          api.get("/news/trending"),
        ]);
        setNews(a.data || []);
        setTrending(b.data || []);
      } catch (err) {
        console.error("Home API failed:", err);
        setNews([]);
        setTrending([]);
      }
    })();
  }, []);

  const featured = news.find((n) => n.featured) || news[0];
  const rest = news.filter((n) => (n.slug || n.id) !== (featured?.slug || featured?.id));
  const byCat = (slug) => rest.filter((n) => n.category_slug === slug).slice(0, 3);

  return (
    <div className="pt-16 pb-16">
      <section className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-8">
            <FeaturedHero article={featured} />
          </div>
          <aside className="lg:col-span-4 bg-[var(--bg-panel)] border border-[var(--border-soft)] p-6" data-testid="trending-sidebar">
            <div className="flex items-center gap-2 mb-6">
              <Flame className="w-4 h-4 text-yellow-400" />
              <h3 className="font-mono text-xs uppercase tracking-[0.2em] text-[var(--text-tertiary)]">Trending now</h3>
            </div>
            <ol className="space-y-5">
              {trending.slice(0, 3).map((t, i) => (
                <li key={t.id} className="flex gap-4 group">
                  <span className="font-display text-3xl font-bold text-zinc-700 group-hover:text-yellow-400 transition">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <Link
                    to={"/news/" + (t.slug || t.id)}
                    data-testid={"trending-item-" + t.id}
                    className="text-sm text-[var(--text-secondary)] group-hover:text-yellow-400 transition leading-snug"
                  >
                    {t.title}
                  </Link>
                </li>
              ))}
            </ol>
            <GainersLosers />
          </aside>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 md:px-10 mt-20">
        <div className="flex items-end justify-between mb-8">
          <h2 className="font-display font-bold text-3xl md:text-4xl text-[var(--text-primary)] tracking-tight">Latest News</h2>
          <Link to="/news" className="text-sm font-mono uppercase tracking-widest text-yellow-400 hover:underline" data-testid="see-all-news">
            See all <ArrowRight className="inline w-3 h-3" />
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {rest.slice(0, 6).map((n) => (
            <NewsCard key={n.id} article={n} />
          ))}
        </div>
      </section>

      {/* Portfolio Tracker CTA */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 mt-20">
        <div className="relative bg-[var(--bg-panel)] border border-[var(--border-soft)] overflow-hidden">
          <div className="absolute inset-0 opacity-10" style={{
            backgroundImage: "radial-gradient(circle at 80% 50%, #facc15 0%, transparent 50%), radial-gradient(circle at 20% 50%, #3b82f6 0%, transparent 50%)",
          }} />
          <div className="relative flex flex-col md:flex-row items-center justify-between gap-6 p-8 md:p-10">
            <div className="flex items-start gap-5">
              <div className="w-12 h-12 bg-yellow-400/10 border border-yellow-400/30 flex items-center justify-center flex-shrink-0">
                <Wallet className="w-6 h-6 text-yellow-400" />
              </div>
              <div>
                <div className="font-mono text-xs uppercase tracking-[0.2em] text-[var(--text-tertiary)] mb-1">Free Portfolio Tracker</div>
                <h3 className="font-display font-bold text-xl md:text-2xl text-[var(--text-primary)] tracking-tight">
                  Track your crypto holdings in real time.
                </h3>
                <p className="mt-2 text-sm text-[var(--text-secondary)] max-w-lg">
                  Add your coins, set your buy price, and watch your portfolio update live. No app needed — built directly into MediaCrypto.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3 flex-shrink-0">
              <Link to={user ? "/portfolio" : "/register"}>
                <button className="bg-yellow-400 text-black font-bold uppercase tracking-wide px-6 py-3 hover:bg-yellow-500 transition text-sm whitespace-nowrap">
                  {user ? "Open Portfolio →" : "Try it free →"}
                </button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <PollWidget />

      {[
        { slug: "bitcoin", label: "Bitcoin" },
        { slug: "ethereum", label: "Ethereum" },
        { slug: "altcoins", label: "Altcoins" },
        { slug: "market-analysis", label: "Market Analysis" },
      ].map((cat) => {
        const items = byCat(cat.slug);
        if (!items.length) return null;
        return (
          <section key={cat.slug} className="max-w-[1400px] mx-auto px-6 md:px-10 mt-20">
            <div className="flex items-end justify-between mb-8 border-b border-[var(--border-soft)] pb-4">
              <h2 className="font-display font-bold text-2xl md:text-3xl text-[var(--text-primary)]">
                <span className="text-yellow-400">/</span> {cat.label}
              </h2>
              <Link
                to={"/categories/" + cat.slug}
                data-testid={"cat-link-" + cat.slug}
                className="text-xs font-mono uppercase tracking-widest text-[var(--text-tertiary)] hover:text-yellow-400"
              >
                More →
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {items.map((n) => <NewsCard key={n.id} article={n} />)}
            </div>
          </section>
        );
      })}

      <section className="max-w-[1400px] mx-auto px-6 md:px-10 mt-24">
        <div className="relative bg-[var(--bg-panel)] border border-[var(--border-soft)] p-10 md:p-16 overflow-hidden">
          <div className="absolute inset-0 opacity-20" style={{
            backgroundImage: "radial-gradient(circle at 20% 50%, #facc15 0%, transparent 40%), radial-gradient(circle at 80% 30%, #3b82f6 0%, transparent 40%)",
          }} />
          <div className="relative max-w-2xl">
            <Users className="w-8 h-8 text-yellow-400 mb-4" />
            <h2 className="font-display font-bold text-4xl md:text-5xl text-[var(--text-primary)] tracking-tight">
              Join the MediaCrypto Community.
            </h2>
            <p className="mt-4 text-[var(--text-secondary)] text-lg">
              Share alpha, debate macro, and build with traders, developers, and analysts who care about signal.
            </p>
            <Link to="/community" data-testid="cta-community-link">
              <button className="mt-8 bg-yellow-400 text-black font-bold uppercase tracking-wide px-8 py-4 hover:bg-yellow-500 transition">
                Enter the Feed →
              </button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}